/**
 * **„Gibt es das schon?"** (Nils, 18.08.2026: *„Eine neue Aufgabe soll ebenfalls einen
 * «Prüfen, ob schon existiert»-Knopf haben, dass der dann einmal schaut, ob schon so eine
 * Aufgabe existiert in der Richtung."*)
 *
 * ## Warum das im Client rechnet und nicht auf dem Server
 *
 * Weil der ganze Aufgabenbestand ohnehin im Client liegt (Leitplanke 2 in `CLAUDE.md`) und
 * die Antwort damit **sofort** da ist – ohne Spinner, ohne Rundreise, ohne Kosten. Genau so
 * steht es auch in [E-10](../../../../docs/ENTSCHEIDUNGEN.md): Ähnlichkeitssuche im Code,
 * weil MariaDB kein pgvector hat. Eine KI zu fragen wäre die teuerste Art, „Medienkonverter"
 * zweimal zu lesen.
 *
 * ## Warum zwei Maße und nicht eines
 *
 * | Maß | fängt … | verliert … |
 * |---|---|---|
 * | **Wörter** | „Report für Medienkonverter" ↔ „Medienkonverter-Report" | „Konverter" ↔ „Medienkonverter" |
 * | **Dreiergruppen** | Fugen-s, Bindestriche, Beugung, Tippfehler | nichts Inhaltliches, dafür viel Rauschen |
 *
 * Im Deutschen ist das kein Randfall, sondern der Normalfall: zusammengesetzte Wörter
 * bedeuten dasselbe und sehen anders aus. Wörter allein fänden die Hälfte nicht,
 * Dreiergruppen allein hielten „Bericht" und „Gericht" für nah verwandt. Zusammen tragen
 * sie sich gegenseitig.
 *
 * ## Was das Ergebnis nicht ist
 *
 * Kein Urteil. Die Liste sagt *„sieh dir das an"*, nicht *„das ist dasselbe"* – deshalb
 * blockiert sie das Anlegen nicht und gibt einen Wert aus, keine Ja/Nein-Antwort.
 */

/**
 * Wörter, die in Aufgabentiteln überall vorkommen und deshalb nichts unterscheiden.
 *
 * Bewusst knapp gehalten: jedes Wort auf dieser Liste ist eines, das nicht mehr helfen kann.
 * „Fehler" oder „Kunde" stehen **nicht** darauf – sie kommen oft vor und sagen trotzdem
 * etwas.
 */
const FUELLWOERTER = new Set([
  'der',
  'die',
  'das',
  'den',
  'dem',
  'des',
  'ein',
  'eine',
  'einen',
  'einem',
  'einer',
  'und',
  'oder',
  'aber',
  'wenn',
  'dann',
  'als',
  'auch',
  'noch',
  'nur',
  'nicht',
  'für',
  'fuer',
  'von',
  'vom',
  'mit',
  'ohne',
  'bei',
  'beim',
  'aus',
  'auf',
  'über',
  'ueber',
  'unter',
  'nach',
  'vor',
  'zum',
  'zur',
  'ist',
  'sind',
  'war',
  'wird',
  'werden',
  'soll',
  'sollen',
  'kann',
  'können',
  'koennen',
  'muss',
  'müssen',
  'muessen',
  'wie',
  'was',
  'dass',
  'sich',
  'man',
  'wir',
  'bitte',
])

/**
 * Umlaute und Schärfungen auflösen, alles klein, alles Nichtbuchstabige zu Leerzeichen.
 *
 * Die Umlaut-Auflösung ist der Punkt: „Prüfen" und „Pruefen" stehen in derselben Datenbank
 * nebeneinander, und ohne sie haben die beiden **keinen einzigen** gemeinsamen Buchstaben
 * an der entscheidenden Stelle.
 */
export function normalisieren(text: string): string {
  return text
    .toLowerCase()
    .replaceAll('ä', 'ae')
    .replaceAll('ö', 'oe')
    .replaceAll('ü', 'ue')
    .replaceAll('ß', 'ss')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

/** Wörter ab drei Zeichen, ohne Füllwörter. Zahlen bleiben – Ticketnummern unterscheiden. */
export function woerter(text: string): Set<string> {
  const menge = new Set<string>()
  for (const wort of normalisieren(text).split(' ')) {
    if (wort.length < 3) continue
    if (FUELLWOERTER.has(wort)) continue
    menge.add(wort)
  }
  return menge
}

/** Dreiergruppen über den zusammengezogenen Text – die Brücke über Fugen und Beugungen. */
export function dreiergruppen(text: string): Set<string> {
  const roh = normalisieren(text).replaceAll(' ', '')
  const menge = new Set<string>()
  for (let i = 0; i + 3 <= roh.length; i++) menge.add(roh.slice(i, i + 3))
  return menge
}

function schnittmenge<T>(a: Set<T>, b: Set<T>): number {
  let treffer = 0
  for (const wert of a) if (b.has(wert)) treffer++
  return treffer
}

/** Sørensen-Dice: doppelte Schnittmenge durch die Summe – 1 bei gleich, 0 bei fremd. */
function dice<T>(a: Set<T>, b: Set<T>): number {
  if (a.size === 0 || b.size === 0) return 0
  return (2 * schnittmenge(a, b)) / (a.size + b.size)
}

export interface AehnlicheAufgabe<T> {
  eintrag: T
  /** 0 bis 1. Ab 0.34 wird angezeigt – siehe `SCHWELLE`. */
  wert: number
}

/**
 * **Ab wann eine Aufgabe gezeigt wird.**
 *
 * Gemessen an echten Titeln aus dem Bestand: unter 0,3 stehen dort Paare, die nur ein
 * Allerweltswort teilen („Fehler beim Export" ↔ „Fehler beim Anmelden"). Wer für jede neue
 * Aufgabe fünf solche Zeilen bekommt, liest beim dritten Mal keine mehr – und übersieht die
 * echte Dublette, die dazwischen steht.
 */
export const SCHWELLE = 0.34

/** Wie viele Treffer höchstens angezeigt werden. Mehr als acht liest niemand. */
export const HOECHSTENS = 8

/**
 * Den Entwurf gegen einen Bestand halten.
 *
 * ## Zwei Lesarten, die stärkere gewinnt
 *
 * **Titel gegen Titel** ist die scharfe: dort zählen Wörter *und* Dreiergruppen, und nur
 * dort können Werte über 0,6 entstehen. Zwei Aufgaben sind dieselbe Sache, wenn ihre Titel
 * dasselbe meinen.
 *
 * **Entwurf gegen Titel** ist die weite: sie nimmt die Beschreibung dazu, aber nur mit
 * Wörtern und gedämpft. Sie fängt den Fall *„Neue Meldung aus dem Support"*, bei dem im
 * Titel nichts und im Text alles steht. Dreiergruppen bleiben hier außen vor – über einen
 * Absatz Fließtext findet jede Zeichenfolge irgendwo ein Zuhause, und aus Zufall würde
 * Ähnlichkeit.
 *
 * Das Maß ist **einseitig**: gefragt wird, wie viel vom *bestehenden* Titel im Entwurf
 * vorkommt, nicht umgekehrt. Sonst sinkt jede Ähnlichkeit, je mehr jemand schreibt – und
 * ausgerechnet die sorgfältig beschriebene Aufgabe fände ihre Dublette nicht.
 */
export function aehnlicheFinden<T extends { title: string }>(
  entwurf: { titel: string; beschreibung?: string },
  bestand: readonly T[],
  optionen: { schwelle?: number; hoechstens?: number } = {},
): AehnlicheAufgabe<T>[] {
  const titel = entwurf.titel.trim()
  if (titel.length < 3) return []

  const titelWoerter = woerter(titel)
  const titelGruppen = dreiergruppen(titel)
  const alleWoerter = new Set([...titelWoerter, ...woerter(entwurf.beschreibung ?? '')])

  const schwelle = optionen.schwelle ?? SCHWELLE
  const treffer: AehnlicheAufgabe<T>[] = []

  for (const eintrag of bestand) {
    const andere = woerter(eintrag.title)
    if (andere.size === 0) continue

    const ausTitel =
      0.65 * (schnittmenge(andere, titelWoerter) / andere.size) +
      0.35 * dice(dreiergruppen(eintrag.title), titelGruppen)

    /** Gedämpft: was nur im Fließtext steht, ist ein Hinweis, keine Übereinstimmung. */
    const ausText = 0.55 * (schnittmenge(andere, alleWoerter) / andere.size)

    const wert = Math.min(1, Math.max(ausTitel, ausText))
    if (wert >= schwelle) treffer.push({ eintrag, wert })
  }

  return treffer.sort((a, b) => b.wert - a.wert).slice(0, optionen.hoechstens ?? HOECHSTENS)
}
